// src/pages/TermsPage.tsx
// Página de Termos de Uso - Identidade GRAIA

import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import './TermsPage.css';

const sections = [
    { id: 'intro', title: 'Introdução' },
    { id: 'finalidade', title: '1. Finalidade da Plataforma' },
    { id: 'conteudo', title: '2. Conteúdo e Propriedade Intelectual' },
    { id: 'conta', title: '3. Conta de Usuário' },
    { id: 'uso', title: '4. Uso da Plataforma e Responsabilidades' },
    { id: 'pagamentos', title: '5. Pagamentos e Assinaturas' },
    { id: 'seguranca', title: '6. Segurança e Proteções Técnicas' },
    { id: 'privacidade', title: '7. Dados e Privacidade' },
    { id: 'grupos', title: '8. Grupos de Estudo' },
    { id: 'assistente', title: '9. Assistente Graia' },
    { id: 'fase', title: '10. Plataforma em Fase Inicial' },
    { id: 'limitacoes', title: '11. Limitações de Responsabilidade' },
    { id: 'alteracoes', title: '12. Alterações nos Termos' },
    { id: 'contato', title: '13. Contato e Suporte' },
    { id: 'finais', title: '14. Disposições Finais' },
];

const TermsPage: React.FC = () => {
    const [activeSection, setActiveSection] = useState('intro');
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const sectionEls = sections.map(s => document.getElementById(s.id));
            const scrollY = window.scrollY + 120;
            for (let i = sectionEls.length - 1; i >= 0; i--) {
                const el = sectionEls[i];
                if (el && el.offsetTop <= scrollY) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 90;
            window.scrollTo({ top, behavior: 'smooth' });
        }
        setMenuOpen(false);
    };

    return (
        <div className="gt-wrapper">

            {/* ── HEADER ── */}
            <header className="gt-header">
                <div className="gt-header-inner">
                    <div className="gt-logo-wrap">
                        <img
                            src="/graia-logo.png"
                            alt="GRAIA"
                            className="gt-logo-img"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'block';
                            }}
                        />
                        <span className="gt-logo-text" style={{ display: 'none' }}>GRAIA</span>
                    </div>
                    <div className="gt-header-spacer" />
                </div>
            </header>

            {/* ── HERO ── */}
            <div className="gt-hero">
                <div className="gt-hero-inner">
                    <p className="gt-hero-tag">Documento Legal</p>
                    <h1 className="gt-hero-title">Termos de Uso</h1>
                    <p className="gt-hero-sub">
                        Condições para utilização da plataforma GRAIA
                    </p>
                    <p className="gt-hero-date">Última atualização: Março de 2026</p>
                </div>
            </div>

            {/* ── LAYOUT PRINCIPAL ── */}
            <div className="gt-layout">

                {/* Sidebar / Índice desktop */}
                <aside className="gt-sidebar">
                    <p className="gt-sidebar-title">Índice</p>
                    <nav className="gt-sidebar-nav">
                        {sections.map(s => (
                            <button
                                key={s.id}
                                className={`gt-sidebar-item${activeSection === s.id ? ' gt-sidebar-item--active' : ''}`}
                                onClick={() => scrollTo(s.id)}
                                type="button"
                            >
                                {s.title}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Índice mobile (accordion) */}
                <div className="gt-mobile-index">
                    <button
                        className="gt-mobile-index-btn"
                        onClick={() => setMenuOpen(v => !v)}
                        type="button"
                    >
                        <span>Índice</span>
                        <ChevronDown size={16} className={menuOpen ? 'gt-rotate' : ''} />
                    </button>
                    {menuOpen && (
                        <nav className="gt-mobile-index-list">
                            {sections.map(s => (
                                <button
                                    key={s.id}
                                    className="gt-mobile-index-item"
                                    onClick={() => scrollTo(s.id)}
                                    type="button"
                                >
                                    {s.title}
                                </button>
                            ))}
                        </nav>
                    )}
                </div>

                {/* ── CONTEÚDO ── */}
                <main className="gt-content">

                    {/* INTRODUÇÃO */}
                    <section id="intro" className="gt-section">
                        <h2 className="gt-section-title">Introdução</h2>
                        <p>
                            Estes Termos de Uso regulam o acesso e a utilização da plataforma <strong>GRAIA</strong>,
                            desenvolvida para apoiar estudantes em cursos extensos — com ênfase em medicina — por meio
                            de ferramentas de estudo ativo, planejamento, colaboração e acompanhamento de desempenho.
                        </p>
                        <p>
                            Ao criar uma conta, acessar ou utilizar qualquer funcionalidade da plataforma, o usuário
                            declara que leu, compreendeu e concorda integralmente com todos os termos e condições aqui
                            estabelecidos. Caso não concorde com alguma disposição, recomendamos que não prossiga
                            com o cadastro.
                        </p>
                        <div className="gt-notice gt-notice--info">
                            <strong>Aviso importante:</strong> A plataforma GRAIA encontra-se em fase inicial de uso
                            público. Embora extensos testes internos tenham sido realizados sem identificação de erros
                            críticos, algumas funcionalidades podem apresentar instabilidades pontuais. Problemas podem
                            ser reportados pelos canais de suporte indicados neste documento.
                        </div>
                    </section>

                    {/* 1 */}
                    <section id="finalidade" className="gt-section">
                        <h2 className="gt-section-title">1. Finalidade da Plataforma</h2>
                        <p>
                            GRAIA é uma plataforma educacional de suporte ao estudo, com caráter auxiliar e
                            complementar. Suas principais ferramentas incluem:
                        </p>
                        <ul className="gt-list">
                            <li>Memorização ativa via flashcards e algoritmo de repetição espaçada</li>
                            <li>Banco de questões com simulados customizados e provas oficiais</li>
                            <li>Planejamento de estudos com agenda semanal, metas e revisões automatizadas</li>
                            <li>Participação em grupos de estudo com rankings e comparativos de desempenho</li>
                            <li>Apostilas interativas com anotações, grifos e quizzes embutidos</li>
                            <li>Estatísticas detalhadas de evolução e desempenho</li>
                        </ul>
                        <p>
                            A plataforma <strong>não substitui</strong> instituições de ensino, avaliações acadêmicas
                            oficiais, orientação profissional ou qualquer forma de instrução formal. GRAIA é uma
                            ferramenta auxiliar de apoio ao processo de aprendizagem individual.
                        </p>
                        <div className="gt-notice gt-notice--info">
                            <strong>Sem garantia de resultados:</strong> GRAIA não assegura nem garante resultados
                            acadêmicos, aprovação em provas ou desempenho específico do usuário. Tais resultados
                            dependem de múltiplos fatores individuais, incluindo dedicação, disciplina, base prévia
                            e métodos de estudo adotados pelo próprio usuário.
                        </div>
                    </section>

                    {/* 2 */}
                    <section id="conteudo" className="gt-section">
                        <h2 className="gt-section-title">2. Conteúdo e Propriedade Intelectual</h2>

                        <h3 className="gt-subsection-title">2.1 Conteúdo da Plataforma</h3>
                        <p>
                            Todo o conteúdo produzido e disponibilizado pela equipe GRAIA — incluindo flashcards
                            oficiais, apostilas, resumos, questões, estruturas pedagógicas e materiais associados —
                            é protegido por direitos autorais e demais legislações aplicáveis.
                        </p>
                        <p>É expressamente proibido ao usuário:</p>
                        <ul className="gt-list">
                            <li>Reproduzir, copiar ou redistribuir qualquer conteúdo da plataforma</li>
                            <li>Compartilhar materiais com terceiros não cadastrados</li>
                            <li>Disponibilizar conteúdos em outras plataformas, sites ou serviços</li>
                            <li>Comercializar, revender ou repassar materiais, total ou parcialmente</li>
                            <li>Utilizar o conteúdo para fins que não sejam o estudo pessoal dentro da plataforma</li>
                        </ul>

                        <h3 className="gt-subsection-title">2.2 Licença de Uso</h3>
                        <p>
                            GRAIA concede ao usuário uma licença <strong>limitada, não exclusiva, intransferível
                                e revogável</strong> para acesso e uso do conteúdo da plataforma, exclusivamente para
                            fins pessoais e educacionais, durante o período em que a conta estiver ativa.
                        </p>
                        <p>
                            Essa licença não confere ao usuário qualquer direito de propriedade sobre os conteúdos,
                            sendo vedada qualquer forma de uso fora dos limites estabelecidos nestes Termos. O
                            encerramento da conta, por qualquer motivo, implica revogação imediata desta licença.
                        </p>

                        <h3 className="gt-subsection-title">2.3 Conteúdo Gerado pelo Usuário</h3>
                        <p>
                            O usuário é <strong>integralmente responsável</strong> por qualquer conteúdo que inserir
                            na plataforma, incluindo anotações, comentários em grupos e quaisquer outros registros
                            de sua autoria. GRAIA não se responsabiliza por conteúdos inseridos por usuários que
                            violem direitos de terceiros, legislação aplicável ou estes Termos de Uso.
                        </p>
                        <p>
                            GRAIA poderá remover, a exclusivo critério e sem aviso prévio, conteúdos que violem
                            estes Termos, bem como suspender ou encerrar a conta do usuário responsável.
                        </p>
                    </section>

                    {/* 3 */}
                    <section id="conta" className="gt-section">
                        <h2 className="gt-section-title">3. Conta de Usuário</h2>
                        <p>
                            Para acessar as funcionalidades completas da plataforma, é necessário criar uma conta
                            individual. Ao se cadastrar, o usuário compromete-se a:
                        </p>
                        <ul className="gt-list">
                            <li>Fornecer informações verdadeiras, completas e atualizadas no cadastro</li>
                            <li>Manter a confidencialidade de seus dados de acesso (e-mail e senha)</li>
                            <li>Não compartilhar a conta com terceiros</li>
                            <li>Notificar imediatamente a equipe em caso de acesso não autorizado</li>
                        </ul>
                        <p>A conta é <strong>pessoal e intransferível</strong>.</p>

                        <h3 className="gt-subsection-title">3.1 Aceite dos Termos</h3>
                        <p>
                            O aceite destes Termos de Uso é registrado eletronicamente no momento do cadastro,
                            incluindo data e hora do aceite, versão vigente dos Termos e informações técnicas
                            do acesso. Esse registro poderá ser utilizado como prova de concordância em
                            eventuais disputas administrativas ou judiciais.
                        </p>

                        <h3 className="gt-subsection-title">3.2 Idade Mínima e Capacidade Legal</h3>
                        <p>
                            O uso da plataforma é permitido a usuários com <strong>idade mínima de 16 anos</strong>.
                        </p>
                        <p>
                            Usuários menores de 18 anos declaram, ao realizar o cadastro, que possuem autorização
                            expressa de seus pais ou responsáveis legais para utilização da plataforma e aceitação
                            destes Termos de Uso.
                        </p>
                        <p>
                            GRAIA poderá, a qualquer momento, solicitar comprovação dessa autorização, bem como
                            suspender ou encerrar, a exclusivo critério, contas em caso de irregularidade quanto
                            à capacidade legal do usuário.
                        </p>

                        <h3 className="gt-subsection-title">3.3 Sessão Única</h3>
                        <p>
                            GRAIA opera com o modelo de <strong>sessão única por conta</strong>. Isso significa que
                            a conta pode estar ativa em apenas um dispositivo por vez. Ao realizar login em um novo
                            dispositivo, a sessão anterior será automaticamente encerrada.
                        </p>
                        <p>
                            O usuário pode gerenciar os dispositivos conectados pela página
                            <em> "Dispositivos Conectados"</em> e revogar acessos a qualquer momento. Essa medida
                            visa proteger a conta, garantir a integridade dos dados de progresso e prevenir o
                            compartilhamento indevido.
                        </p>

                        <h3 className="gt-subsection-title">3.4 Monitoramento de Sessões e Dispositivos</h3>
                        <p>
                            Para garantir a segurança da conta e a conformidade com a política de uso individual,
                            a equipe GRAIA monitora as sessões ativas de cada usuário, registrando informações como
                            tipo de dispositivo e sistema operacional utilizados em cada acesso.
                        </p>
                        <p>
                            Esse monitoramento tem finalidade exclusivamente protetiva: permite ao usuário
                            visualizar e gerenciar os dispositivos conectados à sua conta pela página
                            <em> "Dispositivos Conectados"</em>, revogar acessos suspeitos e ser alertado sobre
                            atividades incomuns.
                        </p>
                        <p>
                            O compartilhamento de conta com terceiros é considerado violação dos presentes Termos.
                            Contas com padrão de uso incompatível com o acesso individual poderão ser suspensas
                            ou encerradas, a exclusivo critério, sem direito a reembolso, conforme disposto na seção 4.
                        </p>

                        <h3 className="gt-subsection-title">3.4 Exclusão de Conta pelo Usuário</h3>
                        <p>
                            O usuário pode solicitar a exclusão de sua conta a qualquer momento pelos canais de
                            suporte indicados na seção 13. Ao excluir a conta:
                        </p>
                        <ul className="gt-list">
                            <li>O acesso à plataforma e a todas as funcionalidades premium é encerrado imediatamente</li>
                            <li>Assinaturas ativas são canceladas, sem direito a reembolso proporcional pelo período
                                não utilizado, salvo disposição legal em contrário</li>
                            <li>Dados pessoais identificáveis serão removidos, podendo ser retidos dados
                                anonimizados conforme descrito na seção 7</li>
                            <li>A conta não poderá ser recuperada após a exclusão definitiva</li>
                        </ul>
                    </section>

                    {/* 4 */}
                    <section id="uso" className="gt-section">
                        <h2 className="gt-section-title">4. Uso da Plataforma e Responsabilidades</h2>
                        <p>
                            O usuário compromete-se a utilizar a plataforma GRAIA de forma ética, responsável e
                            compatível com sua finalidade educacional. É expressamente proibido:
                        </p>
                        <ul className="gt-list">
                            <li>Utilizar a plataforma para fins ilegais, ilícitos ou abusivos</li>
                            <li>Praticar atos difamatórios, discriminatórios ou que violem direitos de terceiros</li>
                            <li>Interferir no funcionamento técnico da plataforma</li>
                            <li>Comercializar o acesso à conta ou funcionalidades da plataforma</li>
                            <li>Compartilhar credenciais de acesso com terceiros</li>
                            <li>Realizar engenharia reversa, tentativas de exploração ou qualquer ação que comprometa
                                a segurança ou integridade do sistema</li>
                        </ul>

                        <h3 className="gt-subsection-title">4.1 Suspensão e Encerramento de Conta</h3>
                        <p>
                            O acesso à plataforma poderá ser suspenso ou encerrado, a exclusivo critério de GRAIA,
                            a qualquer momento, nos seguintes casos:
                        </p>
                        <ul className="gt-list">
                            <li>Violação de qualquer disposição destes Termos de Uso</li>
                            <li>Uso indevido da plataforma ou de suas funcionalidades</li>
                            <li>Compartilhamento de conta com terceiros</li>
                            <li>Tentativas de fraude, engenharia reversa ou exploração do sistema</li>
                            <li>Comportamento que comprometa a segurança, integridade ou funcionamento da plataforma</li>
                        </ul>
                        <p>
                            GRAIA reserva-se o direito de realizar o encerramento <strong>com ou sem aviso
                                prévio</strong>, a depender da gravidade da conduta. Nos casos de violação destes Termos,
                            o usuário não terá direito a qualquer tipo de reembolso, indenização ou compensação,
                            inclusive em relação a valores já pagos por assinaturas ativas.
                        </p>

                        <p>
                            GRAIA reserva-se o direito de encerrar contas a seu exclusivo critério sempre que
                            identificar risco à plataforma, à comunidade de usuários, violação destes Termos
                            ou comportamento incompatível com a finalidade educacional da plataforma, sem que
                            seja necessário apresentar justificativa detalhada ao usuário.
                        </p>
                    </section>

                    {/* 5 */}
                    <section id="pagamentos" className="gt-section">
                        <h2 className="gt-section-title">5. Pagamentos e Assinaturas</h2>
                        <p>
                            GRAIA oferece funcionalidades premium mediante assinatura paga. Os pagamentos são
                            processados por plataformas terceiras (como MercadoPago), sujeitas às políticas e
                            termos de uso próprios.
                        </p>
                        <p>
                            Ao contratar qualquer plano pago, o usuário declara estar ciente das funcionalidades
                            oferecidas e concorda integralmente com os presentes Termos de Uso.
                        </p>
                        <ul className="gt-list">
                            <li><strong>Dados financeiros:</strong> Informações de cartão de crédito ou débito não
                                são armazenadas por GRAIA; são tratadas exclusivamente pela plataforma de pagamento.</li>
                            <li><strong>Renovação automática:</strong> As assinaturas poderão ser renovadas
                                automaticamente ao final de cada período contratado, salvo cancelamento prévio
                                realizado pelo usuário antes da data de renovação.</li>
                            <li><strong>Cancelamento:</strong> O cancelamento de assinatura remove o acesso às
                                funcionalidades premium ao fim do período já pago, mas os dados de progresso e
                                histórico de estudo são mantidos, podendo ser acessados caso o usuário renove o plano.</li>
                            <li><strong>Alterações de preço:</strong> GRAIA reserva-se o direito de ajustar preços e
                                condições de assinatura, com comunicação prévia aos usuários ativos.</li>
                        </ul>

                        <h3 className="gt-subsection-title">5.1 Direito de Arrependimento</h3>
                        <p>
                            Nos termos do art. 49 do Código de Defesa do Consumidor, o usuário poderá solicitar
                            o cancelamento com reembolso integral no prazo de até <strong>7 (sete) dias corridos</strong> após
                            a contratação. O direito de arrependimento será respeitado nos termos da legislação aplicável.
                        </p>
                        <p>
                            Solicitações de arrependimento devem ser realizadas pelo e-mail de suporte indicado
                            na seção 13, com o assunto <em>"Arrependimento de Compra"</em>.
                        </p>

                        <h3 className="gt-subsection-title">5.2 Política de Estorno e Chargeback</h3>
                        <p>
                            Fora do prazo legal de arrependimento, solicitações de estorno ou cancelamento de
                            cobrança poderão ser recusadas nos seguintes casos:
                        </p>
                        <ul className="gt-list">
                            <li>Uso efetivo da plataforma, ainda que parcial, durante o período contratado</li>
                            <li>Violação dos Termos de Uso pelo usuário</li>
                            <li>Suspensão ou encerramento da conta por conduta indevida</li>
                            <li>Tentativas de fraude ou contestação indevida de pagamento</li>
                        </ul>
                        <p>
                            A abertura de disputas ou chargebacks junto à operadora de pagamento <strong>sem contato
                                prévio com o suporte de GRAIA</strong> poderá resultar na suspensão imediata da conta.
                            Eventuais reembolsos, quando aplicáveis por obrigação legal, serão analisados caso a caso.
                        </p>
                        <div className="gt-notice gt-notice--warn">
                            Em caso de cobrança indevida ou problemas com pagamentos, o usuário deve contatar
                            o suporte imediatamente pelo e-mail indicado na seção 13, antes de abrir qualquer
                            disputa junto à operadora.
                        </div>
                    </section>

                    {/* 6 */}
                    <section id="seguranca" className="gt-section">
                        <h2 className="gt-section-title">6. Segurança e Proteções Técnicas</h2>
                        <p>
                            Com o objetivo de proteger o conteúdo educacional e garantir a integridade da
                            experiência de estudo, a plataforma GRAIA adota mecanismos técnicos de segurança e
                            antipirataria, que incluem:
                        </p>
                        <ul className="gt-list">
                            <li>Restrição de cópia, colagem e extração de texto nas páginas de estudo, flashcards e apostilas</li>
                            <li>Monitoramento de comportamentos incompatíveis com o uso educacional individual</li>
                            <li>Sessão única por conta, com encerramento automático em caso de acesso paralelo</li>
                            <li>Autenticação via JWT com validade controlada</li>
                            <li>Registro de atividades suspeitas para fins de auditoria</li>
                        </ul>
                        <p>
                            Ao utilizar a plataforma, o usuário reconhece e aceita a existência dessas medidas,
                            que visam garantir a equidade entre usuários e preservar os direitos autorais dos
                            conteúdos disponibilizados.
                        </p>
                        <p>
                            A plataforma é fornecida <strong>no estado em que se encontra</strong>, sem garantias
                            de funcionamento ininterrupto, ausência total de erros ou adequação a expectativas
                            específicas do usuário. GRAIA não assegura nem garante disponibilidade contínua,
                            podendo ocorrer pausas temporárias por manutenção programada, atualizações de sistema
                            ou falhas técnicas imprevistas. Tais interrupções não geram direito a reembolso
                            ou compensação.
                        </p>
                    </section>

                    {/* 7 */}
                    <section id="privacidade" className="gt-section">
                        <h2 className="gt-section-title">7. Dados e Privacidade</h2>

                        <h3 className="gt-subsection-title">7.1 Dados Coletados</h3>
                        <p>GRAIA coleta os seguintes tipos de dados para funcionamento da plataforma:</p>
                        <ul className="gt-list">
                            <li><strong>Dados pessoais:</strong> Nome, endereço de e-mail e foto de perfil (quando
                                fornecidos via cadastro ou login social como Google OAuth)</li>
                            <li><strong>Dados de uso:</strong> Progresso em flashcards e questões (acertos, erros,
                                tempo), sessões de estudo e participação em grupos</li>
                            <li><strong>Dados técnicos:</strong> Tipo de dispositivo, sistema operacional e logs de
                                acesso, para fins de segurança e melhoria do sistema</li>
                        </ul>

                        <h3 className="gt-subsection-title">7.2 Uso dos Dados</h3>
                        <p>Os dados coletados são utilizados para:</p>
                        <ul className="gt-list">
                            <li>Autenticação e personalização da experiência de estudo</li>
                            <li>Funcionamento do algoritmo de repetição espaçada e recomendações</li>
                            <li>Geração de estatísticas individuais e coletivas (nos grupos)</li>
                            <li>Melhoria contínua dos algoritmos e funcionalidades da plataforma</li>
                            <li>Detecção de uso indevido e proteção da plataforma</li>
                        </ul>

                        <h3 className="gt-subsection-title">7.3 Armazenamento e Exclusão</h3>
                        <p>
                            Os dados são armazenados em servidores seguros. O usuário pode solicitar a exclusão
                            de sua conta a qualquer momento. Após a exclusão, dados pessoais identificáveis serão
                            removidos, podendo ser retidos dados anonimizados e agregados para análise de
                            desempenho e melhoria do sistema.
                        </p>
                        <p>
                            GRAIA não vende, cede nem comercializa dados pessoais de usuários a terceiros,
                            exceto quando exigido por lei ou mediante autorização expressa do usuário.
                        </p>
                        <p>
                            O tratamento de dados pessoais realizado pela plataforma ocorre com base nas
                            hipóteses legais previstas na Lei Geral de Proteção de Dados (LGPD), incluindo
                            execução de contrato, cumprimento de obrigação legal e legítimo interesse.
                        </p>
                    </section>

                    {/* 8 */}
                    <section id="grupos" className="gt-section">
                        <h2 className="gt-section-title">8. Grupos de Estudo</h2>
                        <p>
                            A funcionalidade de grupos de estudo permite que usuários comparem progresso,
                            participem de rankings e estudem de forma colaborativa.
                        </p>
                        <ul className="gt-list">
                            <li>A participação em grupos é voluntária e realizada mediante código de convite</li>
                            <li>Dados de desempenho (pontuação, ranking e estatísticas) são visíveis para todos os membros do grupo</li>
                            <li>Administradores de grupo podem remover membros, sem que a plataforma GRAIA se responsabilize por conflitos internos</li>
                            <li>A exclusão de um grupo segue processo democrático com votação entre membros</li>
                            <li>GRAIA não se responsabiliza por condutas de outros usuários dentro dos grupos</li>
                        </ul>
                        <p>
                            O uso indevido da funcionalidade de grupos (incluindo assédio, divulgação de conteúdo
                            externo ou uso para fins comerciais) é vedado e pode resultar em suspensão ou
                            encerramento de conta, a exclusivo critério.
                        </p>
                    </section>

                    {/* 9 */}
                    <section id="assistente" className="gt-section">
                        <h2 className="gt-section-title">9. Assistente Graia</h2>
                        <p>
                            A plataforma disponibiliza o <strong>Assistente Graia</strong>, uma ferramenta de
                            organização pessoal que permite ao usuário registrar horários, sessões de estudo,
                            metas, revisões e compromissos acadêmicos.
                        </p>
                        <p>
                            Todas as informações inseridas no Assistente Graia são fornecidas
                            <strong> voluntariamente pelo próprio usuário</strong> e permanecem vinculadas
                            exclusivamente à sua conta. A plataforma não realiza coleta ativa de dados externos,
                            não acessa agendas de terceiros, calendários, e-mails ou qualquer outra fonte
                            externa de informação. Todos os dados exibidos no Assistente Graia são inseridos
                            diretamente pelo próprio usuário.
                        </p>
                        <p>
                            O uso dessas informações ocorre de forma estritamente funcional — limitado a viabilizar
                            recursos como organização de agenda, acompanhamento de progresso e sugestões de revisão —
                            não sendo utilizadas para fins comerciais, publicitários ou compartilhadas com terceiros.
                        </p>
                        <p>
                            O usuário mantém controle total sobre os dados inseridos, podendo editá-los ou
                            excluí-los a qualquer momento dentro da própria plataforma.
                        </p>
                        <p>
                            GRAIA adota medidas técnicas razoáveis para proteção das informações armazenadas, não
                            se responsabilizando, contudo, por falhas decorrentes de uso indevido da conta pelo
                            próprio usuário ou por terceiros que tenham obtido acesso indevido às suas credenciais.
                        </p>
                        <div className="gt-notice gt-notice--info">
                            <strong>Privacidade garantida:</strong> Os dados inseridos no Assistente Graia
                            pertencem exclusivamente ao usuário e são utilizados apenas para o funcionamento
                            técnico da ferramenta. GRAIA não os utiliza para fins comerciais, publicitários
                            ou de compartilhamento com terceiros.
                        </div>
                    </section>

                    {/* 10 */}
                    <section id="fase" className="gt-section">
                        <h2 className="gt-section-title">10. Plataforma em Fase Inicial</h2>
                        <p>
                            GRAIA está em fase inicial de operação pública. Isso significa que:
                        </p>
                        <ul className="gt-list">
                            <li>Funcionalidades podem não estar completamente estáveis em todos os cenários de uso</li>
                            <li>A plataforma reserva-se o direito de atualizar, modificar ou remover funcionalidades
                                sem aviso prévio, visando melhorias</li>
                            <li>Erros e inconsistências pontuais podem ocorrer, mesmo após extensos testes internos</li>
                            <li>O usuário pode — e é encorajado a — relatar problemas pelos canais de suporte disponíveis</li>
                        </ul>
                        <div className="gt-notice gt-notice--info">
                            O retorno de usuários nos primeiros meses é fundamental para o aprimoramento da
                            plataforma. Reporte qualquer problema ou sugestão pelo e-mail de suporte.
                        </div>
                    </section>

                    {/* 11 */}
                    <section id="limitacoes" className="gt-section">
                        <h2 className="gt-section-title">11. Limitações de Responsabilidade</h2>
                        <p>GRAIA não assegura nem se responsabiliza por:</p>
                        <ul className="gt-list">
                            <li>Perdas de dados causadas por falhas de conexão, dispositivos do usuário ou fatores externos</li>
                            <li>Danos indiretos decorrentes do uso ou impossibilidade de uso da plataforma</li>
                            <li>Conteúdos inseridos por usuários que infrinjam direitos de terceiros</li>
                            <li>Conflitos entre membros de grupos de estudo</li>
                            <li>Interrupções temporárias por manutenção, atualização ou falhas técnicas</li>
                            <li>Resultados acadêmicos, aprovações ou desempenho do usuário em avaliações externas</li>
                            <li>Funcionamento ininterrupto, livre de erros ou plenamente adequado às expectativas
                                específicas de cada usuário</li>
                        </ul>
                    </section>

                    {/* 12 */}
                    <section id="alteracoes" className="gt-section">
                        <h2 className="gt-section-title">12. Alterações nos Termos</h2>
                        <p>
                            Os presentes Termos de Uso poderão ser modificados a qualquer momento, conforme
                            evolução da plataforma ou exigências legais. Em caso de alterações relevantes:
                        </p>
                        <ul className="gt-list">
                            <li>O usuário será notificado por e-mail e/ou por aviso dentro da plataforma</li>
                            <li>A data da última atualização, exibida no topo deste documento, será revisada</li>
                            <li>O uso continuado da plataforma após a publicação das alterações implica aceitação
                                das novas condições</li>
                        </ul>
                        <p>
                            Recomenda-se que o usuário revise este documento periodicamente para manter-se
                            informado sobre eventuais atualizações.
                        </p>
                    </section>

                    {/* 13 */}
                    <section id="contato" className="gt-section">
                        <h2 className="gt-section-title">13. Contato e Suporte</h2>
                        <p>
                            Para dúvidas, solicitações, reportes de problemas ou questões relacionadas a dados
                            pessoais, entre em contato pelos canais oficiais:
                        </p>
                        <div className="gt-contact-grid">
                            <div className="gt-contact-item">
                                <span className="gt-contact-label">E-mail de suporte</span>
                                <a href="mailto:plataformagraia@gmail.com" className="gt-contact-value">plataformagraia@gmail.com</a>
                            </div>
                            <div className="gt-contact-item">
                                <span className="gt-contact-label">Horário de atendimento</span>
                                <span className="gt-contact-value">Dias úteis, horário comercial</span>
                            </div>
                        </div>
                        <p className="gt-contact-note">
                            Solicitações de exclusão de dados, arrependimento de compra ou dúvidas sobre
                            privacidade devem ser enviadas com o assunto correspondente para o e-mail acima.
                        </p>
                    </section>

                    {/* 14 */}
                    <section id="finais" className="gt-section">
                        <h2 className="gt-section-title">14. Disposições Finais</h2>
                        <p>
                            A utilização da plataforma implica aceitação integral destes Termos de Uso.
                            O descumprimento de qualquer cláusula poderá resultar em restrições de acesso,
                            suspensão ou encerramento definitivo da conta, a exclusivo critério, sem direito
                            a reembolso.
                        </p>
                        <p>
                            Estes Termos são regidos pelas leis brasileiras, em especial pela Lei Geral de
                            Proteção de Dados (LGPD — Lei nº 13.709/2018) e pelo Código de Defesa do Consumidor
                            (CDC — Lei nº 8.078/1990). Fica eleito o foro da comarca de domicílio do usuário
                            para dirimir eventuais conflitos, salvo disposição legal em contrário.
                        </p>
                    </section>

                </main>
            </div>

            {/* ── FOOTER ── */}
            <footer className="gt-footer">
                <img src="/graia-logo.png" alt="GRAIA" className="gt-footer-logo"
                    onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        const fb = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                        if (fb) fb.style.display = 'inline';
                    }}
                />
                <span className="gt-footer-logo-text" style={{ display: 'none' }}>GRAIA</span>
                <p className="gt-footer-copy">© 2026 GRAIA. Todos os direitos reservados.</p>
                <p className="gt-footer-legal">
                    Plataforma educacional sujeita à LGPD (Lei nº 13.709/2018) e ao CDC (Lei nº 8.078/1990).
                </p>
            </footer>

        </div>
    );
};

export default TermsPage;